/* eslint-disable max-len */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import useTranslation from '@src/hooks/useTranslation';
import styled, { css } from 'styled-components';
import { isEmpty, isNull } from 'lodash';

import { IColors } from '@src/types/theme';
import useCurrentCourse from '@src/hooks/useCurrentCourse';
import useRating from '@src/hooks/useRating';
import { getColorTheme } from '@src/store/selectors/theme';
import { getCourseCertificate } from '@src/store/selectors/course';
import { getDomain } from '@src/store/selectors';
import { formatToPercentage } from '@src/utils/common';
import { hasHomeworks } from '@src/utils/course';
import { getIsDisabledComment } from '@src/utils/hardcode';
import { REVIEW_MODAL_TYPE_ADD, REVIEW_MODAL_TYPE_EDIT } from '@src/const/course';

import {
	CertificateIcon, CertificateDoneIcon, HomeworkIcon, ReviewIcon, ReviewDoneIcon,
} from '@src/components/common/icon';
import AddReviewModal from '@src/components/course/review/addReviewModal';
import MyReviewModal from '@src/components/course/review/myReviewModal';
import HomeworkPercentage from './homeworkPercentage';

const iconStyle = css`
	@media ${props => props.theme.device.tablet} {
		width: 40px;
		height: 40px;
	}

	@media ${props => props.theme.device.smTablet} {
		width: 40px;
		height: 40px;
	}

	@media ${props => props.theme.device.mobile} {
		width: 40px;
		height: 40px;
	}
`;

const InteractionWrapper = styled.div`
	display: flex;
	justify-content: center;
`;

const InteractionItem = styled.div`
	display: flex;
	align-items: center;
	cursor: ${props => (props.cursorActive && !props.isDisabled ? 'pointer' : 'unset')};

	&:not(:last-child) {
		&:after {
			content:'|';
			margin: 0 24px;
			color: #dddddd;
		}
	}

	@media ${props => props.theme.device.lgDesktop} {
		flex-direction: column;

		&:not(:last-child) {
			margin-right: 70px;

			&:after {
				display: none;
			}
		}
	}

	@media ${props => props.theme.device.desktop} {
		flex-direction: column;

		&:not(:last-child) {
			margin-right: 66px;

			&:after {
				display: none;
			}
		}
	}

	@media ${props => props.theme.device.tablet} {
		&:not(:last-child) {
			&:after {
				margin: 0 48px;
			}
		}
	}
`;

const CertificateIconWrapper = styled(CertificateIcon)`
	${iconStyle}
`;

const CertificateDoneIconWrapper = styled(CertificateDoneIcon)`
	${iconStyle}
`;

const HomeworkIconWrapper = styled(HomeworkIcon)`
	${iconStyle}
`;

const ReviewIconWrapper = styled(ReviewIcon)`
	${iconStyle}
`;

const ReviewDoneIconWrapper = styled(ReviewDoneIcon)`
	${iconStyle}
`;

const InteractionText = styled.div`
	margin: 0 0 0 8px;
	font-size: 16px;
	color: ${props => props.textColor};

	@media ${props => props.theme.device.lgDesktop} {
		margin: 8px 0 0;
	}

	@media ${props => props.theme.device.desktop} {
		margin: 8px 0 0;
	}
`;

interface IInteraction {
	className?: string;
}

const Interaction = ({ className }: IInteraction): JSX.Element => {
	const { t } = useTranslation();
	const [isAddModalActive, setAddModalActive] = useState(false);
	const [isReviewModalActive, setReviewModalActive] = useState(false);
	const { currentCourse: {
		id: courseId, chapters, submit_homework, total_homework, has_been_rated, has_certificate, learning_progress,
	}, handleSubmitHomework, handleGetCertificate } = useCurrentCourse();
	const { courseRatings, handleAddCourseRating, handleEditCourseRating } = useRating({ courseId, onlyMe: true });
	const { colors } = useSelector(getColorTheme) as { colors: IColors };
	const courseCertificate = useSelector(getCourseCertificate)[courseId];
	const domain = useSelector(getDomain);
	const isDisabled = getIsDisabledComment(domain);

	const showCertificate = has_certificate && learning_progress === 100;

	const handleOpenAddModal = () => { setAddModalActive(true); };
	const handleCloseAddModal = () => { setAddModalActive(false); };
	const handleOpenReviewModal = () => { setReviewModalActive(true); };
	const handleCloseReviewModal = () => { setReviewModalActive(false); };
	const handleSubmitReview = ({ modalType, message, rating }) => (
		(modalType === REVIEW_MODAL_TYPE_ADD) ?
			handleAddCourseRating(message, rating) :
			handleEditCourseRating(message, rating)
	);
	const handleEdit = () => {
		handleCloseReviewModal();
		handleOpenAddModal();
	};

	return (
		<>
			<InteractionWrapper className={className}>
				{showCertificate ? (
					<InteractionItem
						cursorActive
						onClick={handleGetCertificate}
					>
						{isNull(courseCertificate) ?
							<CertificateIconWrapper /> : <CertificateDoneIconWrapper mainColor={colors.primary} />}
						<InteractionText textColor="#101010">
							{isNull(courseCertificate) ?
								t('course:get_certificate') : t('course:already_get_certificate')}
						</InteractionText>
					</InteractionItem>
				) : (
					<InteractionItem
						cursorActive={hasHomeworks(chapters)}
						onClick={hasHomeworks(chapters) ? handleSubmitHomework : null}
					>
						{hasHomeworks(chapters) ? (
							submit_homework > 0 ? (
								<>
									<HomeworkPercentage
										percentage={formatToPercentage(submit_homework, total_homework)}
									/>
									<InteractionText textColor="#101010">
										{t('course:homework_progress')}
									</InteractionText>
								</>
							) : (
								<>
									<HomeworkIconWrapper />
									<InteractionText textColor="#101010">
										{t('course:hand_in_homework')}
									</InteractionText>
								</>
							)
						) : (
							<>
								<HomeworkIconWrapper circleColor="#DDDDDD" pathColor="#BFBFBF" />
								<InteractionText textColor="#BFBFBF">{t('course:hand_in_homework')}</InteractionText>
							</>
						)}
					</InteractionItem>
				)}
				<InteractionItem
					cursorActive
					isDisabled={isDisabled}
					onClick={() => {
						if (isDisabled) return;
						if (has_been_rated) {
							handleOpenReviewModal();
						} else {
							handleOpenAddModal();
						}
					}}
				>
					{has_been_rated ? (
						<>
							<ReviewDoneIconWrapper reviewColor={colors.primary} />
							<InteractionText textColor="#101010">{t('course:reviewed')}</InteractionText>
						</>
					) : (
						<>
							<ReviewIconWrapper {...(isDisabled ? { iconColor: '#BFBFBF', strokeColor: '#BFBFBF' } : {})} />
							<InteractionText textColor={isDisabled ? '#BFBFBF' : '#101010'}>{t('course:write_review')}</InteractionText>
						</>
					)}
				</InteractionItem>
			</InteractionWrapper>
			{isAddModalActive && (
				<AddReviewModal
					modalType={has_been_rated ? REVIEW_MODAL_TYPE_EDIT : REVIEW_MODAL_TYPE_ADD}
					review={isEmpty(courseRatings) ? {} : courseRatings[0]}
					isActive={isAddModalActive}
					handleSubmitReview={handleSubmitReview}
					handleClose={handleCloseAddModal}
				/>
			)}
			{has_been_rated && isReviewModalActive && (
				<MyReviewModal
					review={courseRatings[0]}
					isActive={isReviewModalActive}
					handleEdit={handleEdit}
					handleClose={handleCloseReviewModal}
				/>
			)}
		</>
	);
};

export default Interaction;
